/**
 * Derive a human-readable PDF filename from the fields we already capture
 * on `daily_pdfs`. The output is used in three places:
 *   1. Content-Disposition of presigned R2 URLs (so the browser shows a
 *      sensible name in the tab title and on Save-As).
 *   2. Display label on Source PDFs tables (replacing the raw UUID).
 *   3. The "View PDF" link text in dense lists where space is tight.
 *
 * Lives in `shared` so the API endpoint and the UI produce identical labels
 * for the same input — no drift.
 *
 * Heuristic:
 *   - If `email_subject` already looks like a HeavyJob-style report header
 *     (e.g. "TM 3.16.26 - C60A01 & Misc Log"), use it verbatim.
 *   - Otherwise compose `<initials> <m.d.yy> - <subject-tail>` from what we
 *     have, falling back gracefully when fields are missing.
 *
 * Output is sanitized for both filesystem and HTTP-header safety.
 */
export interface PdfFilenameInput {
  email_subject?: string | null;
  foreman_name?: string | null;
  /** 'YYYY-MM-DD' as stored in the DB. */
  report_date?: string | null;
}

const HEAVYJOB_HEADER = /^[A-Za-z]{1,4}\s+\d{1,2}\.\d{1,2}\.\d{2,4}\b/;
// Strip control chars (intentional — dangerous in filenames + HTTP headers)
// plus characters that break Windows/macOS/Linux paths and Content-Disposition.
// eslint-disable-next-line no-control-regex
const ILLEGAL = /[\x00-\x1f"\\/:*?<>|]/g;
const COLLAPSE_WS = /\s+/g;
const MAX_LEN = 80;

export function derivePdfFilename(input: PdfFilenameInput): string {
  const subject = (input.email_subject ?? '').trim();

  // Case 1: subject is already HeavyJob-shaped — preserve it.
  if (subject && HEAVYJOB_HEADER.test(subject)) {
    return sanitize(subject) + '.pdf';
  }

  // Case 2: compose from structured fields.
  const initials = input.foreman_name ? toInitials(input.foreman_name) : '';
  const shortDate = input.report_date ? toShortDate(input.report_date) : '';
  const head = [initials, shortDate].filter(Boolean).join(' ');
  const tail = subject ? subject : '';
  const body = head && tail ? `${head} - ${tail}` : head || tail || 'daily-tm-report';
  return sanitize(body) + '.pdf';
}

function toInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

function toShortDate(iso: string): string {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  if (!y || !m || !d) return iso;
  const yy = y.slice(-2);
  return `${parseInt(m, 10)}.${parseInt(d, 10)}.${yy}`;
}

function sanitize(s: string): string {
  return s.replace(ILLEGAL, '').replace(COLLAPSE_WS, ' ').trim().slice(0, MAX_LEN);
}
